import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useSelector } from 'react-redux';

import { AxiosResponse } from 'axios';

import {
    RECORDING_MAIN_METADATA_LANGUAGE,
    getNextCatchupEpisodeUrl,
    getNextNpvrEpisodeUrl,
    getNextVodEpisodeUrl,
} from 'config/api';

import { StreamTypes } from 'store/player/actions.types';
import { selectStandardChannelById } from 'store/programs/selectors';
import {
    BTVProgrammesResponse,
    VodResponse,
} from 'store/railsBuilder/reducer.types';
import { RecordingResponse } from 'store/recordings/reducer.types';
import { ReduxStore } from 'store/types';

import parseVideoItem from 'services/data-manager/parsing/parseVideoItem';
import { VideoItem } from 'services/data-manager/types';

import NagraInstance from 'utils/axios/nagra/DefaultInstance.ts';
import { convertBooleanStringToBoolean } from 'utils/convertBooleanStringToBoolean';
import debugLog from 'utils/debugLog';

import { isLive } from 'screens/Epg/utils/program';
import { SwitchPlayerSource } from 'screens/PlayerModal/types';

type Params = {
    currentlyPlayingEpisode?: VideoItem;
    playerProgress: number;
    videoDuration: number;
    isPaused: boolean;
    isVod: boolean;
    isNpvr: boolean;
    isCatchup: boolean;
    seeking: boolean;
    playerReady: boolean;
    handleSwitchPlayerSource: SwitchPlayerSource;
    handleResetPlayer: () => void;
};

const BINGE_WATCHING_THRESHOLD_VOD = 20;
const BINGE_WATCHING_THRESHOLD = 15;

type BingeWatchingReturn = {
    isBingeWatchingVisible: boolean;
    bingewatchingCountdown: number;
    nextEpisode: VideoItem | null;
    onCloseBingeWatching: () => void;
    onPlayNextEpisode: () => void;
    bingeTransitionInProgressRef: React.RefObject<boolean>;
};

function useBingeWatching({
    currentlyPlayingEpisode,
    playerProgress,
    videoDuration,
    isPaused,
    isVod,
    isNpvr,
    isCatchup,
    seeking,
    playerReady,
    handleSwitchPlayerSource,
    handleResetPlayer,
}: Params): BingeWatchingReturn {
    const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const bingeTransitionInProgressRef = useRef(false);
    const channel = useSelector((state: ReduxStore) =>
        selectStandardChannelById(state, currentlyPlayingEpisode?.channelId),
    );
    const enableBingeWatching =
        isVod ||
        convertBooleanStringToBoolean(channel?.editorial?.enableBingeWatching);

    const threshold = useMemo(() => {
        const guardTimeEnd = currentlyPlayingEpisode?.guardTimeEnd ?? 0;
        const bingeWatchingTimeout =
            channel?.editorial.bingeWatchingTimeout ?? BINGE_WATCHING_THRESHOLD;
        return isVod
            ? BINGE_WATCHING_THRESHOLD_VOD
            : guardTimeEnd + Number(bingeWatchingTimeout);
    }, [isVod, currentlyPlayingEpisode, channel]);

    const [isBingeWatchingVisible, setIsBingeWatchingVisible] = useState(false);
    const [bingewatchingCountdown, setBingewatchingCountdown] =
        useState(threshold);
    const [nextEpisode, setNextEpisode] = useState<VideoItem | null>(null);
    const [userDismissed, setUserDismissed] = useState(false);

    const isBingeWatchingApplicable = isVod || isNpvr || isCatchup;

    const bingeWatchingThreshold = useMemo(() => {
        if (!videoDuration || videoDuration <= threshold) {
            return 0;
        }

        return videoDuration - threshold - 1;
    }, [videoDuration, threshold]);

    const isNextEpisodeAvailable = (
        currentSeason: number,
        currentEpisode: number,
        nextVideoItem: VideoItem,
    ) => {
        const now = Math.round(Date.now() / 1000);

        // Check if the next videoItem is upcoming
        if (nextVideoItem.startTime != null && nextVideoItem.startTime > now) {
            return false;
        }
        // Check if the next videoItem is live
        if (isLive(nextVideoItem)) {
            return false;
        }

        // Check season and episode number
        if (nextVideoItem.seasonNumber != null) {
            const nextItemSeason = Number(nextVideoItem.seasonNumber);
            const nextItemEpisode = Number(nextVideoItem.episodeNumber);

            if (currentSeason === nextItemSeason) {
                if (nextItemEpisode != null) {
                    return nextItemEpisode === currentEpisode + 1;
                }
            } else {
                return (
                    nextItemSeason === currentSeason + 1 &&
                    nextItemEpisode === 1
                );
            }
        }

        return false;
    };

    const getNextEpisode = useCallback(
        async (current: VideoItem): Promise<VideoItem | null> => {
            try {
                if (
                    !current.seriesId ||
                    current.seasonNumber == null ||
                    current.episodeNumber == null
                ) {
                    return null;
                }

                let url: string;
                let response:
                    | AxiosResponse<BTVProgrammesResponse>
                    | AxiosResponse<RecordingResponse>
                    | AxiosResponse<VodResponse>;
                let nextEpisodeData: VideoItem | null = null;

                const currentSeasonNumber = Number(current.seasonNumber);
                const currentEpisodeNumber = Number(current.episodeNumber);

                if (isCatchup) {
                    url = getNextCatchupEpisodeUrl(
                        current.seriesId,
                        currentSeasonNumber,
                        currentEpisodeNumber,
                    );
                    response = await NagraInstance.get<BTVProgrammesResponse>(
                        url,
                    );

                    if (response.data.programmes[0]) {
                        const program = response.data.programmes[0];
                        nextEpisodeData = parseVideoItem({
                            channel,
                            program,
                            streamType: StreamTypes.catchup,
                        });
                    }
                } else if (isNpvr) {
                    if (!current.channelId) {
                        return null;
                    }
                    url = getNextNpvrEpisodeUrl(
                        current.seriesId,
                        current.channelId,
                        currentSeasonNumber,
                        currentEpisodeNumber,
                    );
                    response = await NagraInstance.get<RecordingResponse>(url);

                    if (response.data.recordingSet[0]) {
                        const recording = response.data.recordingSet[0];
                        nextEpisodeData = parseVideoItem({
                            channel,
                            recording,
                            recordingMetadata:
                                recording.metadata[
                                    RECORDING_MAIN_METADATA_LANGUAGE
                                ],
                            streamType: StreamTypes.npvr,
                        });
                    }
                } else if (isVod) {
                    url = getNextVodEpisodeUrl(
                        current.seriesId,
                        currentSeasonNumber,
                        currentEpisodeNumber,
                    );
                    response = await NagraInstance.get<VodResponse>(url);

                    if (response.data.editorials[0]) {
                        const program = response.data.editorials[0];
                        nextEpisodeData = parseVideoItem({
                            channel,
                            program,
                            streamType: StreamTypes.vod,
                        });
                    }
                }

                if (
                    nextEpisodeData &&
                    isNextEpisodeAvailable(
                        currentSeasonNumber,
                        currentEpisodeNumber,
                        nextEpisodeData,
                    )
                ) {
                    return nextEpisodeData;
                }

                return null;
            } catch (error) {
                debugLog(
                    'Error fetching next episode for binge watching:',
                    error,
                );

                return null;
            }
        },
        [channel, isCatchup, isNpvr, isVod],
    );

    useEffect(() => {
        if (
            currentlyPlayingEpisode &&
            isBingeWatchingApplicable &&
            enableBingeWatching &&
            !nextEpisode &&
            !userDismissed
        ) {
            getNextEpisode(currentlyPlayingEpisode).then((next) => {
                if (next) {
                    setNextEpisode(next);
                }
            });
        }
    }, [
        currentlyPlayingEpisode,
        isBingeWatchingApplicable,
        enableBingeWatching,
        nextEpisode,
        userDismissed,
        getNextEpisode,
    ]);

    const startCountdown = useCallback(() => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }

        countdownIntervalRef.current = setInterval(() => {
            setBingewatchingCountdown((prev) => {
                if (prev < 1) {
                    if (countdownIntervalRef.current) {
                        clearInterval(countdownIntervalRef.current);
                        countdownIntervalRef.current = null;
                    }

                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, []);

    const stopCountdown = () => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
    };

    // Handle paused state - dismiss overlay if user pauses and skips to end
    useEffect(() => {
        if (
            isPaused &&
            isBingeWatchingVisible &&
            playerProgress >= bingeWatchingThreshold
        ) {
            setIsBingeWatchingVisible(false);
            stopCountdown();
        }
    }, [
        isPaused,
        isBingeWatchingVisible,
        playerProgress,
        bingeWatchingThreshold,
    ]);

    const onPlayNextEpisode = useCallback(() => {
        if (nextEpisode) {
            setIsBingeWatchingVisible(false);
            stopCountdown();
            bingeTransitionInProgressRef.current = true;
            handleResetPlayer();

            let streamType = StreamTypes.vod;

            if (isNpvr) {
                streamType = StreamTypes.npvr;
            } else if (isCatchup) {
                streamType = StreamTypes.catchup;
            }

            handleSwitchPlayerSource(nextEpisode, streamType);
        }
    }, [
        nextEpisode,
        handleSwitchPlayerSource,
        handleResetPlayer,
        isNpvr,
        isCatchup,
    ]);

    // Handle countdown reaching 0
    useEffect(() => {
        if (bingewatchingCountdown === 0 && isBingeWatchingVisible) {
            onPlayNextEpisode();
        }
    }, [bingewatchingCountdown, isBingeWatchingVisible, onPlayNextEpisode]);

    // Handle showing binge watching overlay
    useEffect(() => {
        if (
            !isBingeWatchingApplicable ||
            !enableBingeWatching ||
            userDismissed ||
            !videoDuration ||
            !nextEpisode ||
            isPaused ||
            seeking
        ) {
            return;
        }

        const hasReachedThreshold = playerProgress >= bingeWatchingThreshold;

        if (hasReachedThreshold && !isBingeWatchingVisible) {
            setIsBingeWatchingVisible(true);

            // Calculate remaining time for countdown is user seeks past threshold
            const remainingTime =
                Math.floor(videoDuration - playerProgress) - 1;
            const guardTimeEnd = currentlyPlayingEpisode?.guardTimeEnd ?? 0;
            const adjustedCountdown = Math.min(
                remainingTime,
                threshold - guardTimeEnd,
            );

            setBingewatchingCountdown(adjustedCountdown);
            startCountdown();
        }

        // If user skips to the very end (beyond countdown duration), play next ep. immediately
        if (playerProgress >= videoDuration - 1 && !isBingeWatchingVisible) {
            onPlayNextEpisode();
        }
    }, [
        playerProgress,
        bingeWatchingThreshold,
        isBingeWatchingApplicable,
        enableBingeWatching,
        nextEpisode,
        userDismissed,
        isBingeWatchingVisible,
        startCountdown,
        onPlayNextEpisode,
        seeking,
        threshold,
        videoDuration,
        isPaused,
    ]);

    // Handle video item change - reset state
    useEffect(() => {
        setUserDismissed(false);
        setIsBingeWatchingVisible(false);
        setNextEpisode(null);
        setBingewatchingCountdown(threshold);
        stopCountdown();
    }, [currentlyPlayingEpisode, threshold]);

    useEffect(() => {
        // Only reset the transition ref when a new episode is loaded (non-null) and playerReady(loaded),
        // not when the old episode is cleared (null) during the binge watching transition
        if (playerReady && currentlyPlayingEpisode) {
            bingeTransitionInProgressRef.current = false;
        }
    }, [playerReady]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopCountdown();
        };
    }, []);

    const onCloseBingeWatching = () => {
        setIsBingeWatchingVisible(false);
        setUserDismissed(true);
        stopCountdown();
    };

    return {
        isBingeWatchingVisible,
        bingewatchingCountdown,
        nextEpisode,
        onCloseBingeWatching,
        onPlayNextEpisode,
        bingeTransitionInProgressRef,
    };
}

export default useBingeWatching;
